/**
 * Landing — Sparrow's "first 5 seconds" page.
 *
 * Above the fold:
 *   hero with wordmark, tagline, "Try Sparrow" + "Explore samples"
 *   sparkline / data-curve SVG behind the hero (decorative)
 *   3-stat ribbon
 *
 * Below:
 *   value props (browser, formats, offline, etc.)
 *   featured samples strip (3 sample cards → /samples/:id)
 *   bottom CTA
 *
 * Visually distinct from the Quill SPA (quill repo, frontend/) —
 * copper instead of aubergine,
 * DM Serif Display instead of Fraunces, sparkline motif instead of
 * the ink-dot swash.
 */

import { Link } from "react-router-dom"
import { Sparrowmark } from "@/brand/Sparrowmark"
import { SPARROW_URL } from "@/lib/sparrow"
import { SAMPLES } from "@/lib/samples"
import {
  ArrowRight, ArrowUpRight, FileSpreadsheet, Globe, Inbox, Layers,
  Lock, ScanSearch, Sparkles, Wifi,
} from "lucide-react"

export function Landing() {
  return (
    <div className="flex flex-col">
      <Hero />
      <StatRibbon />
      <ValueProps />
      <FeaturedSamples />
      <BottomCta />
    </div>
  )
}

// ── Hero ──────────────────────────────────────────────────────────────

function Hero() {
  return (
    <section className="relative overflow-hidden">
      <div
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(60% 80% at 20% 30%, oklch(0.62 0.09 175 / 0.10), transparent 70%)," +
            "radial-gradient(50% 60% at 85% 75%, oklch(0.55 0.13 45 / 0.13), transparent 70%)",
        }}
      />
      <HeroSparkline />

      <div className="mx-auto max-w-7xl px-6 pt-24 pb-20 sm:pt-28 sm:pb-24">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground mb-8">
          <span className="inline-block w-6 h-px bg-primary" />
          SPSS-style analysis, browser-native
        </div>
        <h1
          className="font-display text-5xl sm:text-6xl md:text-7xl tracking-tight leading-[1.04] text-foreground max-w-5xl"
          style={{ fontFamily: '"Space Grotesk", Inter, "Helvetica Neue", sans-serif' }}
        >
          Statistics in your browser.
          <br />
          <span className="text-primary">No install.</span>
        </h1>
        <p className="mt-6 text-lg text-muted-foreground max-w-2xl leading-relaxed">
          <Sparrowmark size="sm" iconOnly className="text-primary inline-block translate-y-0.5 mr-1" />
          <span className="text-foreground font-medium">Sparrow</span> opens
          .sav, .csv, and .xlsx files and runs descriptives, frequencies,
          crosstabs, custom tables, and charts — entirely in your browser.
          WASM Rust engine, offline-capable, your data never leaves the page.
        </p>

        <div className="mt-10 flex flex-wrap items-center gap-3">
          <a
            href={SPARROW_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary text-primary-foreground font-medium hover:opacity-90 transition-opacity shadow-sm"
          >
            <Sparkles className="h-4 w-4" />
            Try Sparrow
            <ArrowUpRight className="h-4 w-4" />
          </a>
          <Link
            to="/samples"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-border bg-card text-foreground font-medium hover:bg-secondary transition-colors"
          >
            Explore samples
            <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to="/why-sparrow"
            className="ml-2 text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1"
          >
            Why Sparrow <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      </div>
    </section>
  )
}

/** Decorative hero backdrop — a soft sparkline curve, drawn with two
 *  gentle peaks. Sits behind the headline at low opacity. */
function HeroSparkline() {
  return (
    <svg
      aria-hidden
      className="absolute -right-16 top-12 w-[60%] max-w-[820px] opacity-[0.16] pointer-events-none"
      viewBox="0 0 600 320"
      fill="none"
    >
      <defs>
        <linearGradient id="hero-spark" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="oklch(0.42 0.21 263)" stopOpacity="0.9" />
          <stop offset="100%" stopColor="oklch(0.42 0.21 263)" stopOpacity="0" />
        </linearGradient>
      </defs>
      {/* Filled curve */}
      <path
        d="M0 220 C 80 200, 130 90, 200 100 C 270 110, 290 240, 360 220 C 430 200, 470 60, 540 110 C 580 138, 600 150, 600 150 L 600 320 L 0 320 Z"
        fill="url(#hero-spark)"
      />
      {/* Stroke line on top */}
      <path
        d="M0 220 C 80 200, 130 90, 200 100 C 270 110, 290 240, 360 220 C 430 200, 470 60, 540 110 C 580 138, 600 150, 600 150"
        stroke="oklch(0.42 0.21 263)"
        strokeWidth="3"
        strokeLinecap="round"
        fill="none"
      />
      {/* Two data-point dots, one per peak */}
      <circle cx="200" cy="100" r="6" fill="oklch(0.42 0.21 263)" />
      <circle cx="540" cy="110" r="6" fill="oklch(0.78 0.18 165)" />
    </svg>
  )
}

// ── Stats ─────────────────────────────────────────────────────────────

function StatRibbon() {
  const stats = [
    {
      value: "WASM",
      label: "Browser-native engine",
      sub: "Rust compiled to WASM — no plugin, no JVM, no install.",
    },
    {
      value: "Offline",
      label: "Runs without a server",
      sub: "Once loaded, the page works on the train.",
    },
    {
      value: ".sav",
      label: "Reads SPSS files directly",
      sub: ".sav round-trips with labels and variable types intact.",
    },
  ]
  return (
    <section className="border-y border-border bg-card/60">
      <div className="mx-auto max-w-7xl px-6 py-10 grid grid-cols-1 sm:grid-cols-3 gap-8">
        {stats.map((s) => (
          <div key={s.label} className="flex items-baseline gap-4">
            <div
              className="font-display text-5xl text-primary tabular-nums leading-none"
              style={{ fontFamily: '"Space Grotesk", Inter, "Helvetica Neue", sans-serif' }}
            >
              {s.value}
            </div>
            <div className="flex flex-col">
              <div className="font-medium text-foreground">{s.label}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{s.sub}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}

// ── Value props ───────────────────────────────────────────────────────

function ValueProps() {
  const props = [
    {
      icon: Globe,
      title: "Browser-native",
      body:
        "Runs in any modern browser. No installer, no admin rights, no licence-key dance. Open a tab, drop a file, analyse.",
    },
    {
      icon: Wifi,
      title: "Offline-capable",
      body:
        "Once the page has loaded, the WASM engine doesn't need the network. Field work, flaky wifi, locked-down classrooms — Sparrow keeps running.",
    },
    {
      icon: FileSpreadsheet,
      title: "Reads .sav, .csv, .xlsx",
      body:
        "Round-trips SPSS .sav files with value labels and variable types intact. CSV and Excel get sniffed type-by-type so you don't fight the importer.",
    },
    {
      icon: Layers,
      title: "Real procedures",
      body:
        "Descriptives, frequencies, custom tables, crosstabs, charts — the everyday battery a stats user expects. Output goes into a single document you can scroll, print, or share.",
    },
    {
      icon: Lock,
      title: "Your data stays put",
      body:
        "The file you open never leaves the browser tab. No uploads, no temp servers, no analytics on your variables.",
    },
    {
      icon: ScanSearch,
      title: "Built to be read",
      body:
        "Output is APA-flavoured tables you can copy straight into a report — not opaque blobs that require a separate report builder.",
    },
  ]
  return (
    <section id="why" className="mx-auto max-w-7xl px-6 py-20">
      <div className="flex items-end justify-between mb-10">
        <h2
          className="font-display text-3xl sm:text-4xl tracking-tight text-foreground"
          style={{ fontFamily: '"Space Grotesk", Inter, "Helvetica Neue", sans-serif' }}
        >
          What Sparrow does differently
        </h2>
        <div className="text-sm text-muted-foreground hidden sm:block">
          Six things that matter when you open a file.
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {props.map((p) => (
          <div
            key={p.title}
            className="group p-6 rounded-lg border border-border bg-card hover:border-primary/40 transition-colors"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="h-9 w-9 rounded-md bg-primary/10 text-primary inline-flex items-center justify-center group-hover:bg-primary/15 transition-colors">
                <p.icon className="h-5 w-5" />
              </div>
              <h3 className="font-semibold text-foreground">{p.title}</h3>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">{p.body}</p>
          </div>
        ))}
      </div>
    </section>
  )
}

// ── Featured samples strip ────────────────────────────────────────────

function FeaturedSamples() {
  // Surface a sampler — one per category — capped at four cards.
  const seen = new Set<string>()
  const featured = SAMPLES.filter(s => {
    if (seen.has(s.category)) return false
    seen.add(s.category)
    return true
  }).slice(0, 4)

  return (
    <section className="bg-secondary/40 border-y border-border">
      <div className="mx-auto max-w-7xl px-6 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2
              className="font-display text-3xl sm:text-4xl tracking-tight text-foreground"
              style={{ fontFamily: '"Space Grotesk", Inter, "Helvetica Neue", sans-serif' }}
            >
              Featured samples
            </h2>
            <p className="text-muted-foreground mt-2">
              Curated datasets with a canonical analysis baked in — click through for the file.
            </p>
          </div>
          <Link
            to="/samples"
            className="text-sm font-medium text-primary inline-flex items-center gap-1 hover:underline"
          >
            All {SAMPLES.length} samples <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {featured.map((s) => (
            <Link
              key={s.id}
              to={`/samples/${s.id}`}
              className="group p-5 rounded-lg border border-border bg-card hover:border-primary/40 hover:shadow-sm transition-all flex flex-col"
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs uppercase tracking-wider text-muted-foreground">{s.category}</span>
                <span className="text-xs text-muted-foreground border border-border rounded-full px-2 py-0.5 tabular-nums">
                  {s.rows.toLocaleString()} rows
                </span>
              </div>
              <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">{s.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground line-clamp-2 flex-1">{s.canonical}</p>
              <div className="mt-4 text-sm font-medium text-primary inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                Open sample <ArrowRight className="h-3 w-3" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  )
}

// ── Bottom CTA ────────────────────────────────────────────────────────

function BottomCta() {
  return (
    <section className="mx-auto max-w-7xl px-6 py-24">
      <div
        className="relative overflow-hidden rounded-2xl p-10 sm:p-16 text-primary-foreground"
        style={{
          background:
            "linear-gradient(135deg, oklch(0.42 0.21 263) 0%, oklch(0.28 0.18 263) 100%)",
        }}
      >
        <div className="relative z-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div className="max-w-2xl">
            <h2
              className="font-display text-3xl sm:text-4xl tracking-tight"
              style={{ fontFamily: '"Space Grotesk", Inter, "Helvetica Neue", sans-serif' }}
            >
              Try it with your own file.
            </h2>
            <p className="mt-3 text-primary-foreground/85 text-lg leading-relaxed">
              The fastest way to see whether Sparrow fits your workflow is to drop one of your
              own .sav or .csv files into it. Nothing uploads. Nothing installs.
            </p>
          </div>
          <a
            href={SPARROW_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-card text-primary font-semibold hover:opacity-90 transition-opacity shadow-md whitespace-nowrap"
          >
            <Inbox className="h-4 w-4" />
            Open sparrow.dasc.nl
            <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
        <div
          aria-hidden
          className="absolute -right-16 -bottom-16 opacity-25 pointer-events-none text-primary-foreground"
        >
          <Sparrowmark size="xl" iconOnly />
        </div>
      </div>
    </section>
  )
}
